/**
 * Catalog admin (PREVIEW) — a READ-ONLY window onto the Oxbo-M catalog-admin
 * chrome (§22 / §23). Oxbo-Z owns none of this: catalogs are Oxbo-M masters,
 * and this screen only *shows what the Oxbo-M admin looks like* by browsing the
 * same value-API the pickers read (`mApi.getCatalog` → GET /api/m/catalogs/{key}).
 *
 * NO CRUD lives here. Oxbo-Z is a read-only consumer — creating / editing /
 * approving catalog values happens in the real Oxbo-M admin app, reached via the
 * deep-link ↗ (below). Everything on this screen is display-only.
 *
 * Two gates, both role-based (§22c):
 *  1. The whole screen + its nav entry are gated to `data_admin` — a field user
 *     never routes here (guarded here AND at the router/nav so there is no path
 *     in). If a non-admin lands on the route anyway, we render a plain notice
 *     and no catalog data.
 *  2. The deep-link ↗ to the real Oxbo-M admin is only rendered when the loaded
 *     catalog's payload carries an `admin` block AND the user holds its
 *     capability. For non-admins the SERVER omits `admin` entirely, so the URL
 *     never reaches the client (it cannot leak via devtools). We never CSS-hide
 *     a URL that shipped.
 */
import { useState } from "react";
import { useLocale } from "@hooks/useLocale";
import { useAppSelector } from "@app/hooks";
import { CatalogAdminNav } from "./CatalogAdminNav";
import { CatalogDetail } from "./CatalogDetail";
import { DEFAULT_CATALOG_KEY } from "./register";
import "./catalogAdmin.css";

export function CatalogAdmin() {
  const { t } = useLocale();
  const role = useAppSelector((s) => s.prefs.role);
  const [activeKey, setActiveKey] = useState<string>(DEFAULT_CATALOG_KEY);

  // Belt-and-suspenders to the router/nav gate: if a non-admin reaches the
  // route directly, show a notice and fetch nothing. The nav entry + route are
  // already data_admin-only, so this is the last line, not the only one.
  if (role !== "data_admin") {
    return (
      <div>
        <div className="page-head">
          <h1>{t("catadmin.title")}</h1>
          <p className="subtitle">{t("catadmin.subtitle")}</p>
        </div>
        <div className="card">
          <div className="empty-state">{t("catadmin.adminOnly")}</div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="page-head">
        <h1>{t("catadmin.title")}</h1>
        <p className="subtitle">{t("catadmin.subtitle")}</p>
      </div>

      {/* Preview banner — states plainly that Oxbo-Z owns nothing here. */}
      <div className="catadmin-preview-note" role="note">
        <span aria-hidden="true">◑</span>
        {t("catadmin.previewNote")}
      </div>

      <div className="catadmin-split">
        <CatalogAdminNav activeKey={activeKey} onSelect={setActiveKey} />
        <CatalogDetail catalogKey={activeKey} />
      </div>
    </div>
  );
}
